/**
 * buildSubscribeUserCopy
 *
 * Pure function that mirrors Flutter `_setBottomSheetValues`
 * (`lib/presentation/bottom_sheet/subscribe_user_bottom_sheet.dart:131-213`).
 * Derives the title / description / positive-button text / coins-charge flag /
 * price string from the target user, the snapshotted subscription status, and
 * the "should send request" gate. Extracted from `useSubscribeUser` so the copy
 * machine can be unit-tested without rendering React.
 */
import type { l10n } from '@/presentation/locale/l10n';
import { ExclusiveContentSubscriptionStatus } from '@/data/enums/exclusiveSubscriptions/exclusiveContentSubscriptionStatus';
import { MathUtils } from '@/domain/utils/mathUtils';

export interface SubscribeUserCopyTargetUser {
  firstName: string;
  subscriptionCharge: number;
}

export interface SubscribeUserCopy {
  title: string;
  description: string;
  positiveText: string;
  showCoinsCharge: boolean;
  priceText: string;
}

const EMPTY_COPY: SubscribeUserCopy = {
  title: '',
  description: '',
  positiveText: '',
  showCoinsCharge: false,
  priceText: '',
};

export const buildSubscribeUserCopy = (
  targetUser: SubscribeUserCopyTargetUser | null,
  status: ExclusiveContentSubscriptionStatus,
  shouldSendRequest: boolean,
  l10nStrings: Pick<l10n, 'subscribeUser'>,
): SubscribeUserCopy => {
  if (!targetUser) return EMPTY_COPY;

  const firstName = targetUser.firstName;
  const showCoinsCharge = targetUser.subscriptionCharge > 0;
  const priceText = MathUtils.removeTrailingZero(
    MathUtils.coinsFromUSD(targetUser.subscriptionCharge),
  );

  const subscribeUser = l10nStrings.subscribeUser;
  const cancelAddendum = subscribeUser.descriptionCancelAddendum.replace('{firstName}', firstName);
  const coinsAddendum = showCoinsCharge ? subscribeUser.descriptionCoinsAddendum : '';

  let title = '';
  let description = '';
  let positiveText = '';

  switch (status) {
    case ExclusiveContentSubscriptionStatus.inactive: {
      if (shouldSendRequest) {
        title = subscribeUser.titleApprovalRequired;
        description = [
          subscribeUser.descriptionApprovalRequired.replace('{firstName}', firstName),
          coinsAddendum,
          cancelAddendum,
          subscribeUser.descriptionApprovalNotifyAddendum,
        ].join('');
        positiveText = subscribeUser.positiveSendRequest;
      } else {
        title = subscribeUser.titleSubscribe;
        description = [
          subscribeUser.descriptionSubscribe.replace('{firstName}', firstName),
          coinsAddendum,
          cancelAddendum,
        ].join('');
        positiveText = subscribeUser.positiveSubscribe;
      }
      break;
    }
    case ExclusiveContentSubscriptionStatus.active: {
      title = subscribeUser.titleUnsubscribe;
      description = subscribeUser.descriptionUnsubscribe.replace('{firstName}', firstName);
      positiveText = subscribeUser.positiveUnsubscribe;
      break;
    }
    case ExclusiveContentSubscriptionStatus.cancelled: {
      title = subscribeUser.titleSubscribe;
      description = [
        subscribeUser.descriptionSubscribe.replace('{firstName}', firstName),
        coinsAddendum,
        cancelAddendum,
      ].join('');
      positiveText = subscribeUser.positiveSubscribe;
      break;
    }
    case ExclusiveContentSubscriptionStatus.requested: {
      title = subscribeUser.titleCancelRequest;
      description = subscribeUser.descriptionCancelRequest.replace('{firstName}', firstName);
      positiveText = subscribeUser.positiveCancelRequest;
      break;
    }
    default:
      return EMPTY_COPY;
  }

  return { title, description, positiveText, showCoinsCharge, priceText };
};
